import { useState } from 'react';

/**
 * Artikelbild: echtes Produktbild, wenn die API eine bildUrl liefert (ARTICLE_IMAGE_URL_TEMPLATE),
 * sonst eine Illustration nach Artikeltyp, damit der Katalog trotzdem visuell bleibt.
 */
const PLATE = 'M21 10h22a11 11 0 0 1 11 11v22a11 11 0 0 1-11 11H21a11 11 0 0 1-11-11V21a11 11 0 0 1 11-11z';
const ART = {
  kabel: ['M9 32a21 21 0 1 0 42 0a21 21 0 1 0-42 0M16 32a14 14 0 1 0 28 0a14 14 0 1 0-28 0M25 32a5 5 0 1 0 10 0a5 5 0 1 0-10 0M51 32h6v14h-8', '', '#E6EEF9', '#1F4E8C'],
  dose: ['M21 12h22a9 9 0 0 1 9 9v22a9 9 0 0 1-9 9H21a9 9 0 0 1-9-9V21a9 9 0 0 1 9-9zM21 32a11 11 0 1 0 22 0a11 11 0 1 0-22 0M12 24H8M56 24h-4M12 40H8M56 40h-4', '', '#FDF1E3', '#8A4A0B'],
  schutz: ['M24 8h16a4 4 0 0 1 4 4v40a4 4 0 0 1-4 4H24a4 4 0 0 1-4-4V12a4 4 0 0 1 4-4zM27 22h10v16H27zM26 14h12M26 48h12', '', '#F7E9EE', '#8C2F55'],
  rohr: ['M6 36c12 0 14-16 26-16s14 16 26 16M6 46c12 0 14-16 26-16s14 16 26 16M14 33v10M24 24v10M34 21v10M44 26v10M53 34v10', '', '#EEEAF7', '#5B3F99'],
  kanal: ['M6 28h44v20H6zM6 28l8-8h44l-8 8M50 48l8-8V20M17 32v12M28 32v12M39 32v12', '', '#EEEAF7', '#5B3F99'],
  bride: ['M8 46h14M42 46h14M22 46V30a10 10 0 0 1 20 0v16M28 46V31a4 4 0 0 1 8 0v15', '', '#EEEAF7', '#5B3F99'],
  klemme: ['M15 24h34a5 5 0 0 1 5 5v14a5 5 0 0 1-5 5H15a5 5 0 0 1-5-5V29a5 5 0 0 1 5-5zM19 24V14h6v10M29 24V14h6v10M39 24V14h6v10', 'M19 38a3 3 0 1 0 6 0a3 3 0 1 0-6 0M29 38a3 3 0 1 0 6 0a3 3 0 1 0-6 0M39 38a3 3 0 1 0 6 0a3 3 0 1 0-6 0', '#FFF6D6', '#7A5A00'],
  steckdose: [`${PLATE}M18 32a14 14 0 1 0 28 0a14 14 0 1 0-28 0`, 'M24 28a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0-4.4 0M35.6 28a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0-4.4 0M29.8 38a2.2 2.2 0 1 0 4.4 0a2.2 2.2 0 1 0-4.4 0', '#E3F1EB', '#0B6B4E'],
  taster: [`${PLATE}M23 20h18v24H23zM23 32h18`, '', '#E3F1EB', '#0B6B4E'],
  rj: [`${PLATE}M24 24h16v14h-4v4h-8v-4h-4z`, '', '#E3F1EB', '#0B6B4E'],
  leuchte: ['M11 18h42a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H11a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3zM18 40l-4 10M32 40v12M46 40l4 10', '', '#EEF1F0', '#2F3D38'],
  spot: ['M8 22h48v6H8zM14 28a18 14 0 0 0 36 0M20 48l-4 7M32 50v8M44 48l4 7', '', '#EEF1F0', '#2F3D38'],
  box: ['M32 8 56 20v24L32 56 8 44V20zM8 20l24 12 24-12M32 32v24', '', '#F2F4F3', '#41524C'],
};

const RULES = [
  [/netzwerkdose|rj ?45/i, 'rj'],
  [/kabel|leitung|draht|litze/i, 'kabel'],
  [/steckdose|t1[0-9]|t2[0-9]/i, 'steckdose'],
  [/schalter schema|lichtschalter|taster|dimmer/i, 'taster'],
  [/ls-|fi-|leitungsschutz|fehlerstrom|sicherung|automat|rcd/i, 'schutz'],
  [/dose/i, 'dose'],
  [/kanal/i, 'kanal'],
  [/rohr|kir|flex/i, 'rohr'],
  [/bride|schelle|dübel|schraube|befestig/i, 'bride'],
  [/klemme|wago/i, 'klemme'],
  [/spot|downlight|einbau/i, 'spot'],
  [/led|leuchte|lampe|panel/i, 'leuchte'],
];

export function artikelArt(titel = '') {
  return RULES.find(([re]) => re.test(titel))?.[1] ?? 'box';
}

export function ArtikelBild({ artikel, size = 70 }) {
  const [broken, setBroken] = useState(false);
  if (artikel.bildUrl && !broken) {
    return <img src={artikel.bildUrl} alt="" loading="lazy" onError={() => setBroken(true)} />;
  }
  const [d, d2, , ink] = ART[artikelArt(artikel.titel)];
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" stroke={ink} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
      {d2 && <path d={d2} fill={ink} stroke="none" />}
    </svg>
  );
}

export function artikelTint(artikel) {
  if (artikel.bildUrl) return '#FFFFFF';
  return ART[artikelArt(artikel.titel)][2];
}
